import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useUserAuth } from './UserAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/wishlist`;

const WishlistContext = createContext();

export const WishlistProvider = ({ children }) => {
  const { isAuthenticated } = useUserAuth();
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadWishlist = useCallback(async () => {
    if (!isAuthenticated) {
      setWishlist([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(API, { withCredentials: true });
      setWishlist(data.items || []);
    } catch {
      setWishlist([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => { loadWishlist(); }, [loadWishlist]);

  const isInWishlist = useCallback((id) => wishlist.some((i) => i.id === id), [wishlist]);

  const addToWishlist = useCallback(async (product) => {
    if (!isAuthenticated) {
      toast.info('Sign in to save items to your wishlist', {
        action: { label: 'Sign in', onClick: () => { window.location.href = '/login'; } },
      });
      return;
    }
    setWishlist((prev) => [{ ...product, added_at: new Date().toISOString() }, ...prev]);
    try {
      await axios.post(API, { product_id: product.id }, { withCredentials: true });
      toast.success('Added to wishlist');
    } catch {
      setWishlist((prev) => prev.filter((i) => i.id !== product.id));
      toast.error('Could not save to wishlist');
    }
  }, [isAuthenticated]);

  const removeFromWishlist = useCallback(async (id) => {
    setWishlist((prev) => prev.filter((i) => i.id !== id));
    try {
      await axios.delete(`${API}/${id}`, { withCredentials: true });
      toast.success('Removed from wishlist');
    } catch {
      toast.error('Could not remove item');
      loadWishlist();
    }
  }, [loadWishlist]);

  const toggleWishlist = useCallback((product) => (
    isInWishlist(product.id) ? removeFromWishlist(product.id) : addToWishlist(product)
  ), [isInWishlist, addToWishlist, removeFromWishlist]);

  const clearWishlist = useCallback(async () => {
    setWishlist([]);
    await axios.delete(API, { withCredentials: true }).catch(() => loadWishlist());
  }, [loadWishlist]);

  return (
    <WishlistContext.Provider value={{
      wishlist, loading, isAuthenticated, addToWishlist, removeFromWishlist,
      isInWishlist, toggleWishlist, clearWishlist, wishlistCount: wishlist.length,
    }}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) throw new Error('useWishlist must be used within WishlistProvider');
  return context;
};

export default WishlistContext;
